import { Grid, Slider } from '@mui/material';
import { OptimizerConfig } from '@nx-smartmonkey/shared/domain';
import { RoutalSelect, RoutalSelectMultiSelectOption, RoutalSwitch, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useIntl } from 'react-intl';

import {
  getOptimizerConfigMap,
  getOptimizerConfigSmRootMap,
  OptimizerConfigMap,
} from '../../../constants/OptimizerConfig';
import { MetersToSM, SMToMeters } from '../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveUser } from '../../../hooks/user/useRetrieveUser';
import { useRetrieveUserCredentials } from '../../../hooks/user/useRetrieveUserCredentials';
import FormattedMessage from '../../../react-components/FormattedCustomMessage';
import InputText from '../../../react-components/InputText';
import Text from '../../../react-components/Text';

export const RenderOptimizerParameters = ({
  optimizerConfig,
  optimizerConfigMap,
  isAdmin,
  onChange,
  handleEnter,
}: {
  optimizerConfig: OptimizerConfig;
  optimizerConfigMap: Array<OptimizerConfigMap>;
  isAdmin?: boolean;
  onChange?: (...args: any[]) => any; // per al project settings
  handleEnter?: (...args: any[]) => any; // per a plan
}) => {
  const intl = useIntl();
  const { data: project } = useRetrieveActiveProject();

  const getSelectHumanValue = (settings: any, field: any) => {
    const value = settings[field.id] || `noselect`;

    if (!isAdmin) {
      const regularValue = field.options.find((val: any) => val.value === value);
      if (regularValue) {
        return <Text style={{ width: `100%`, textAlign: `start` }} intlId={regularValue.label_id} />;
      }
    }

    return <Text style={{ width: `100%`, textAlign: `start` }}>{value}</Text>;
  };

  const onUpdate = (data: OptimizerConfig) => {
    if (onChange) {
      onChange(data);
    } else if (handleEnter) {
      handleEnter(data);
    }
  };
  return (
    <>
      {optimizerConfig &&
        optimizerConfigMap.map((c: OptimizerConfigMap) => {
          switch (c.type) {
            case `number`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <InputText
                    key={`${c.type}${c.id}`}
                    inputContainerStyles={{
                      width: `100%`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={optimizerConfig.getField(c.id)}
                    handleEnter={(value: any) => {
                      if (handleEnter) {
                        if (!c.max || value <= c.max) {
                          optimizerConfig.updateField({ fieldId: c.id, value: value });
                          handleEnter(optimizerConfig);
                        } else {
                          handleEnter(optimizerConfig);
                        }
                      }
                    }}
                    onChange={(value: any) => {
                      if (onChange) {
                        if (!c.max || value <= c.max) {
                          optimizerConfig.updateField({ fieldId: c.id, value: value });
                          onChange(optimizerConfig);
                        } else {
                          onChange(optimizerConfig);
                        }
                      }
                    }}
                  />
                  <VSpacer medium />
                </Grid>
              );
            case `select`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text variant="body" key={`plabel${c.id}`} intlId={`optimizeroptions.details.${c.id}`} />
                    </RoutalTooltip>
                  ) : (
                    <Text variant="body" key={`plabel${c.id}`} intlId={`optimizeroptions.details.${c.id}`} />
                  )}

                  <VSpacer small />

                  <RoutalSelect
                    id={`select${c.id}`}
                    popoverProps={{
                      options: [
                        {
                          key: `${c.id}menuItemnoselect`,
                          content: c.noTextId ? <FormattedMessage id={`${c.noTextId}`} /> : <span>`-`</span>,
                          onClick: () => {
                            optimizerConfig.updateField({ fieldId: c.id, value: null });
                            onUpdate(optimizerConfig);
                          },
                        },
                        ...(c.options ?? []).map(
                          (options: any): RoutalSelectMultiSelectOption => ({
                            key: `${c.id}menuItem${options.value}`,
                            content: options.label_id ? <FormattedMessage id={`${options.label_id}`} /> : options.label,
                            onClick: () => {
                              const value = options.value;
                              if (value === undefined || value === `noselect`) {
                                optimizerConfig.updateField({ fieldId: c.id, value: null });
                                onUpdate(optimizerConfig);
                              } else {
                                optimizerConfig.updateField({ fieldId: c.id, value: value });
                                onUpdate(optimizerConfig);
                              }
                            },
                          })
                        ),
                      ],
                    }}
                  >
                    {optimizerConfig && optimizerConfig.getField(c.id) ? (
                      getSelectHumanValue(optimizerConfig as any, c)
                    ) : (
                      <Text style={{ width: `100%`, textAlign: `start` }} intlId="not_selected" />
                    )}
                  </RoutalSelect>
                  <VSpacer medium />
                </Grid>
              );
            case `minutes`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <InputText
                    key={`${c.type}${c.id}`}
                    inputContainerStyles={{
                      width: `100%`,
                    }}
                    inputProps={{
                      max: c.max ? Math.round(c.max / 60) : undefined,
                      min: c.min ? Math.round(c.min / 60) : undefined,
                    }}
                    variant="outlined"
                    type="integer"
                    fieldValue={optimizerConfig.getField(c.id) ? optimizerConfig.getField(c.id) / 60 : undefined}
                    handleEnter={(value: any) => {
                      if (handleEnter) {
                        if (!c.max || value * 60 <= c.max) {
                          optimizerConfig.updateField({ fieldId: c.id, value: value * 60 });
                          handleEnter(optimizerConfig);
                        } else {
                          optimizerConfig.updateField({
                            fieldId: c.id,
                            value: c.max || optimizerConfig.getField(c.id),
                          });
                          handleEnter(optimizerConfig);
                        }
                      }
                    }}
                    onChange={(value: any) => {
                      if (onChange) {
                        if (!c.max || value * 60 <= c.max) {
                          optimizerConfig.updateField({ fieldId: c.id, value: value * 60 });
                          onChange(optimizerConfig);
                        } else {
                          optimizerConfig.updateField({
                            fieldId: c.id,
                            value: c.max || optimizerConfig.getField(c.id),
                          });
                          onChange(optimizerConfig);
                        }
                      }
                    }}
                    suffix="min"
                  />
                  <VSpacer medium />
                </Grid>
              );
            case `boolean`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <RoutalSwitch
                    key={`${c.type}${c.id}`}
                    checked={optimizerConfig.getField(c.id)}
                    onChange={(event: any) => {
                      const { checked } = event.target;
                      optimizerConfig.updateField({ fieldId: c.id, value: checked });
                      onUpdate(optimizerConfig);
                    }}
                  />
                  <VSpacer medium />
                </Grid>
              );
            case `boolean_span`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <RoutalSwitch
                    key={`${c.type}${c.id}`}
                    checked={optimizerConfig.getField(c.id) !== 0}
                    onChange={(event: any) => {
                      const { checked } = event.target;
                      optimizerConfig.updateField({ fieldId: c.id, value: checked ? 1 : 0 });
                      onUpdate(optimizerConfig);
                    }}
                  />
                  <VSpacer medium />
                </Grid>
              );
            case `slider`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <Grid container direction="row" alignItems="center" justifyContent="center">
                    <div style={{ width: `80%` }}>
                      <Slider
                        value={optimizerConfig.getField(c.id)}
                        onChange={(event: any, value: any) => {
                          optimizerConfig.updateField({ fieldId: c.id, value: value });
                          onUpdate(optimizerConfig);
                        }}
                        marks={(() => {
                          const marks = [];
                          if (!c.min || !c.max || !c.step) {
                            return [];
                          }
                          for (let value = c.min; value <= c.max; value += c.step) {
                            if (value === c.min) {
                              // MIN with label
                              marks.push({
                                value,
                                label: intl.formatMessage({ id: `optimizeroptions.details.${c.id}.min` }),
                              });
                            } else {
                              // If the maximum is exact, MAX with label
                              if (value === c.max) {
                                marks.push({
                                  value: c.max,
                                  label: intl.formatMessage({ id: `optimizeroptions.details.${c.id}.max` }),
                                });
                              } else {
                                // Intermediate Points
                                marks.push({ value, label: `` });
                              }
                            }
                          }
                          // MAX with label if it is not exact the value
                          if (marks[marks.length - 1].value !== c.max) {
                            marks.push({
                              value: c.max,
                              label: intl.formatMessage({ id: `optimizeroptions.details.${c.id}.max` }),
                            });
                          }
                          return marks;
                        })()}
                        min={c.min}
                        max={c.max}
                        step={c.step}
                      />
                    </div>
                  </Grid>

                  <VSpacer medium />
                </Grid>
              );

            case `meters`:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <InputText
                    key={`number-optimizeroptions-${c.id}`}
                    inputContainerStyles={{
                      width: `100%`,
                    }}
                    variant="outlined"
                    type="number"
                    fieldValue={MetersToSM(optimizerConfig.getField(c.id), project?.units?.distance)}
                    handleEnter={(value: number) => {
                      optimizerConfig.updateField({
                        fieldId: c.id,
                        value: SMToMeters(value, project?.units?.distance),
                      });
                      onUpdate(optimizerConfig);
                    }}
                    onChange={(value: number) => {
                      optimizerConfig.updateField({
                        fieldId: c.id,
                        value: SMToMeters(value, project?.units?.distance),
                      });
                      onUpdate(optimizerConfig);
                    }}
                    inputProps={{
                      min: 0,
                    }}
                    suffix={project?.units?.distance === `mi` ? `yd` : `m`}
                  />
                  <VSpacer medium />
                </Grid>
              );
            default:
              return (
                <Grid item key={`optimizeroptions-${c.id}`}>
                  {c.description ? (
                    <RoutalTooltip
                      id={`optimizeroptions-${c.id}`}
                      placement="top"
                      overlayStyle={{
                        maxWidth: `300px`,
                      }}
                      title={intl.formatMessage({ id: `optimizeroptions.details.${c.description}` })}
                    >
                      <Text
                        variant="body"
                        style={{ marginBottom: `8px` }}
                        key={`plabel${c.id}`}
                        intlId={`optimizeroptions.details.${c.id}`}
                      />
                    </RoutalTooltip>
                  ) : (
                    <Text
                      variant="body"
                      style={{ marginBottom: `8px` }}
                      key={`plabel${c.id}`}
                      intlId={`optimizeroptions.details.${c.id}`}
                    />
                  )}

                  <VSpacer small />

                  <InputText
                    key={`${c.type}${c.id}`}
                    inputContainerStyles={{
                      width: `100%`,
                    }}
                    variant="outlined"
                    fieldValue={optimizerConfig.getField(c.id)}
                    handleEnter={(value: any) => {
                      if (handleEnter) {
                        optimizerConfig.updateField({ fieldId: c.id, value: value });
                        handleEnter(optimizerConfig);
                      }
                    }}
                    onChange={(value: any) => {
                      if (onChange) {
                        optimizerConfig.updateField({ fieldId: c.id, value: value });
                        onChange(optimizerConfig);
                      }
                    }}
                  />
                  <VSpacer medium />
                </Grid>
              );
          }
        })}
    </>
  );
};

type OptimizerPropsConfigComponent = {
  asUser?: string;
  onChange?: (...args: any[]) => any; // per al project settings
  handleEnter?: (...args: any[]) => any; // per a plan
  optimizerConfig?: OptimizerConfig;
};

const OptimizerConfigComponent = ({
  asUser,
  onChange,
  handleEnter,
  optimizerConfig,
}: OptimizerPropsConfigComponent) => {
  const { data: loggedUser } = useRetrieveUser();
  const { data: userCredentials } = useRetrieveUserCredentials();
  if (!optimizerConfig) return null;

  return (
    <>
      <RenderOptimizerParameters
        onChange={onChange}
        handleEnter={handleEnter}
        optimizerConfig={optimizerConfig}
        optimizerConfigMap={getOptimizerConfigMap(loggedUser?.language)}
      />

      {userCredentials?.isStaff(asUser) ? (
        <>
          <VSpacer medium />
          <div
            style={{
              border: `1px solid tomato`,
              padding: `8px`,
              borderRadius: `2px`,
            }}
          >
            <Text variant="body-large" intlId="optimizeroptions.details.admin.title" />
            <VSpacer small />
            <RenderOptimizerParameters
              isAdmin
              onChange={onChange}
              handleEnter={handleEnter}
              optimizerConfig={optimizerConfig}
              optimizerConfigMap={getOptimizerConfigSmRootMap()}
            />
          </div>
          <VSpacer large />
        </>
      ) : null}
    </>
  );
};

export default OptimizerConfigComponent;
